import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {NgForOf, NgIf} from "@angular/common";
import {DeviceDetectorService} from "ngx-device-detector";
import {Services} from '../../../../../model/services';
import {interval, Subject, takeUntil} from 'rxjs';
import {AboutElComponent} from '../about-el/about-el.component';
import {extractFirstParagraph} from '../../../../../functions/extractFirstParagraph';

@Component({
  selector: 'app-slider-horizontal',
  imports: [
    NgForOf,
    AboutElComponent,
    NgIf,
  ],
  templateUrl: './slider-horizontal.component.html',
  styleUrl: './slider-horizontal.component.scss',
  standalone: true,
})
export class SliderHorizontalComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @ViewChild('container') container!: ElementRef;
  @ViewChild('movableElement') movableElement!: ElementRef;
  @Input() grid: number = 1;
  @Input() step: number = 4;
  @Input() elements: any[] = [];
  @Input() test: boolean = false;
  @Input() backgroundContain: boolean = true;
  @Input() autoplay: boolean = false;
  @Input() initialPosition: number = 0;
  @Output() emitActive: EventEmitter<any> = new EventEmitter<any>();

  widthContainer: number = 0;
  heightContainer: number = 0;
  widthEl: number = 0;
  limit: number = 400;
  formattedElements: Services[] = [];
  activeEl: number = 0;
  private isDragging = false;
  private startX = 0;
  private scrollLeft = 0;
  private startLeft = 0;
  isMobile: boolean = false;
  scrolling: boolean = false;
  private dragVelocity: number = 0;
  private lastDragTime: number = 0;
  private lastDragX: number = 0;

  constructor(private deviceService: DeviceDetectorService) {}

  private destroy$ = new Subject<void>();
  private intervalTime = 10000;
  private isMouseDown = false;

  ngOnInit() {
    if (this.autoplay) {
      this.startInterval();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialPosition']) {
      this.scrollToPosition(changes['initialPosition'].currentValue);
    }
    if (changes['elements']) {
      this.getFormattedElements();
      if (this.widthEl > 0) {
        setTimeout(() => {
          this.scrollToInitialPosition();
        }, 0);
      }
    }
  }

  startInterval() {
    this.destroy$.next();
    if (!this.isMouseDown) {
      interval(this.intervalTime)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.scrollToElementSwipe('right');
        });
    }
  }

  onMouseDown() {
    this.isMouseDown = true;
    this.destroy$.next();
  }

  onMouseUp() {
    this.isMouseDown = false;
    if (this.autoplay) {
      this.startInterval();
    }
  }

  handleClick() {
    if (this.autoplay) {
      this.startInterval();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSize() {
    this.widthContainer = this.container.nativeElement.offsetWidth;
    this.heightContainer = this.container.nativeElement.offsetHeight;
    this.widthEl = this.widthContainer;
    if (this.widthContainer < 756) {
      this.limit = 300;
    } else {
      this.limit = 400;
    }
  }

  getFormattedElements() {
    this.formattedElements = this.elements;
    this.formattedElements.forEach((v) => {
      v.firstParagraph = extractFirstParagraph(v.subtitle ?? [])
    })
  }

  scrollToInitialPosition() {
    if (this.formattedElements && this.formattedElements.length > 0 &&
      this.initialPosition >= 0 &&
      this.initialPosition < this.formattedElements.length &&
      this.widthEl > 0) {
      this.activeEl = this.initialPosition;
      const scrollPosition = this.widthEl * this.initialPosition;
      this.movableElement.nativeElement.scrollLeft = scrollPosition;
      this.emitActive.emit(this.elements[this.activeEl]);
    }
  }

  private scrollToPosition(position: number) {
    if (this.formattedElements && this.formattedElements.length > 0 &&
      position >= 0 && position < this.formattedElements.length &&
      this.widthEl > 0) {
      this.activeEl = position;
      const targetPosition = this.widthEl * position;

      this.movableElement.nativeElement.scrollTo({
        left: targetPosition,
        behavior: 'smooth'
      });

      this.emitActive.emit(this.elements[this.activeEl]);
    }
  }

  goToSlide(index: number) {
    if (index >= 0 && index < this.formattedElements.length) {
      this.activeEl = index;
      this.scrollToPosition(index);
    }
  }

  onWheel(event: WheelEvent) {
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      event.preventDefault();
      event.stopPropagation();
      const direction = event.deltaX > 0 ? 'left' : 'right';
      this.scrollToElementSwipe(direction);
      return false;
    }
    return;
  }

  onScroll(event: Event) {
    const scrollLeft = this.movableElement.nativeElement.scrollLeft;
    const newActive = Math.round(scrollLeft / this.widthEl);

    if (newActive !== this.activeEl && newActive >= 0 && newActive < this.formattedElements.length) {
      this.activeEl = newActive;
      this.emitActive.emit(this.elements[this.activeEl]);
    }
  }

  setSliderStyles() {
    return `width: ${this.widthEl}px; min-width: ${this.widthEl}px;`;
  }

  scrollToElementSwipe(type: string) {
    if (type === 'left') {
      const newId = this.activeEl - 1;
      if (newId > -1) {
        this.goToSlide(newId);
      }
    } else {
      const newId = this.activeEl + 1;
      if (newId < this.formattedElements.length) {
        this.goToSlide(newId);
      }
    }
  }

  scrollToElement(value: number) {
    const currentScroll = this.movableElement.nativeElement.scrollLeft;
    const newScroll = currentScroll + value;
    const maxScroll = this.widthEl * (this.formattedElements.length - 1);
    const targetScroll = Math.max(0, Math.min(newScroll, maxScroll));
    this.movableElement.nativeElement.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }

  startDrag(event: MouseEvent | TouchEvent) {
    this.isDragging = true;
    this.startX = event instanceof MouseEvent ? event.pageX : event.touches[0].pageX;
    this.scrollLeft = this.movableElement.nativeElement.scrollLeft;
    this.startLeft = this.scrollLeft;
    this.lastDragX = this.startX;
    this.lastDragTime = Date.now();
    this.movableElement.nativeElement.style.cursor = 'grabbing';
    this.movableElement.nativeElement.style.scrollBehavior = 'auto';
  }

  endDrag(event: MouseEvent | TouchEvent) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.movableElement.nativeElement.style.cursor = 'grab';
    const endX = event instanceof MouseEvent ? event.pageX : (event as TouchEvent).changedTouches[0].pageX;
    const endTime = Date.now();
    const dragDistance = endX - this.startX;
    const dragDuration = endTime - this.lastDragTime;
    if (dragDuration > 0) {
      this.dragVelocity = (endX - this.lastDragX) / dragDuration;
    }
    const threshold = this.widthEl / 8;
    if (Math.abs(dragDistance) > threshold || Math.abs(this.dragVelocity) > 0.5) {
      if (dragDistance > 0 || this.dragVelocity > 0) {
        this.scrollToElementSwipe('left');
      } else {
        this.scrollToElementSwipe('right');
      }
    } else {
      this.scrollToPosition(this.activeEl);
    }
    this.dragVelocity = 0;
    this.movableElement.nativeElement.style.scrollBehavior = 'smooth';
  }
  onTouchMove(event: TouchEvent) {
    if (!this.isDragging) return;
    event.preventDefault();
    const x = event.touches[0].pageX;
    const walk = x - this.startX;
    const newScroll = this.scrollLeft - walk;
    const maxScroll = this.widthEl * (this.formattedElements.length - 1);
    const boundedScroll = Math.max(0, Math.min(newScroll, maxScroll));
    this.movableElement.nativeElement.scrollLeft = boundedScroll;
    const currentTime = Date.now();
    if (currentTime - this.lastDragTime > 16) {
      this.dragVelocity = (x - this.lastDragX) / (currentTime - this.lastDragTime);
      this.lastDragX = x;
      this.lastDragTime = currentTime;
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging || this.isMobile) return;
    event.preventDefault();
    const x = event.pageX;
    const walk = x - this.startX;
    const newScroll = this.scrollLeft - walk;
    const maxScroll = this.widthEl * (this.formattedElements.length - 1);
    const boundedScroll = Math.max(0, Math.min(newScroll, maxScroll));
    this.movableElement.nativeElement.scrollLeft = boundedScroll;
    const currentTime = Date.now();
    if (currentTime - this.lastDragTime > 16) {
      this.dragVelocity = (x - this.lastDragX) / (currentTime - this.lastDragTime);
      this.lastDragX = x;
      this.lastDragTime = currentTime;
    }
  }

  scrollToNearest() {
    const pos = this.movableElement.nativeElement.scrollLeft;
    const start = this.startLeft;
    const del = pos - start;
    const threshold = this.widthEl / 6;
    let newActive = this.activeEl;
    if (Math.abs(del) > threshold) {
      if (del > 0) {
        newActive = Math.max(this.activeEl - 1, 0);
      } else {
        newActive = Math.min(this.activeEl + 1, this.formattedElements.length - 1);
      }
    }
    this.scrollToPosition(newActive);
  }
  private swipeCoord?: [number, number];
  private swipeTime?: number;
  swipe(event: TouchEvent, when: string): void {
    const coord: [number, number] = [event.changedTouches[0].clientX, event.changedTouches[0].clientY];
    const time = new Date().getTime();
    if (when === 'start') {
      this.swipeCoord = coord;
      this.swipeTime = time;
      this.isDragging = true;
      this.startX = coord[0];
      this.scrollLeft = this.movableElement.nativeElement.scrollLeft;
      this.movableElement.nativeElement.style.scrollBehavior = 'auto';
    } else if (when === 'end') {
      if (!this.swipeCoord || !this.swipeTime) return;
      const direction = [coord[0] - this.swipeCoord[0], coord[1] - this.swipeCoord[1]];
      const duration = time - this.swipeTime;
      if (duration < 600 && Math.abs(direction[0]) > 30 && Math.abs(direction[0]) > Math.abs(direction[1] * 3)) {
        if (direction[0] < 0) {
          this.scrollToElementSwipe('right');
        } else {
          this.scrollToElementSwipe('left');
        }
      } else {
        this.scrollToNearest();
      }
      this.isDragging = false;
      this.movableElement.nativeElement.style.scrollBehavior = 'smooth';
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.getFormattedElements();
      this.getSize();
      this.isMobile = this.deviceService.isMobile();
      setTimeout(() => {
        this.scrollToInitialPosition();
      }, 50);
    });
  }

  showPoints() {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const pos = currentScrollTop < window.innerHeight;
    return this.formattedElements.length > 1 && pos;
  }

}
